import { useEffect, useMemo, useState } from "react"

import { EllipsisVertical, Trash } from "lucide-react"
import { Separator, Slider, DefaultTooltip, Button } from "twyne-new.ui"

import { UNIT_OPTIONS } from "../../utils/predefined_sizes"
import { PredefinedArbitraryUnit } from "../PredefinedArbitraryUnit"

import { useFilters } from "./context"

import { PROPS } from "."

export default function FilterProps({ type, displayProps = [], removeProp }) {
  return <>
    <div className='flex flex-col gap-2 max-h-[500px] overflow-y-auto'>
      {PROPS.map(props => {
        if (!displayProps.includes(props.value)) {
          return null
        }

        return <ItemProp
          key={props.value}
          type={type}
          propConfig={props}
          removeProp={removeProp}
        />
      })}
    </div>
  </>
}

const ItemProp = ({ type, propConfig, removeProp }) => {
  const { value: key, label, defaultUnit, onlySlide, slideConfig } = propConfig
  const { filters, handleChange } = useFilters()
  const [slide, setSlide] = useState()
  const value = useMemo(() => filters[type][key]?.value || filters[type][key], [filters, type, key])

  useEffect(() => {
    if (slide === undefined) {
      setSlide(!slideConfig ? false : !value || value.includes('_') || Number(value) < slideConfig?.max)
    }
  }, [slide, value, slideConfig, type])

  const handleChangeKey = newValue => {
    const unit = value ? value.split('_')[1] : defaultUnit
    const valueParsed = typeof newValue === 'number' ? [newValue, unit].filter(item => item !== undefined).join('_') : newValue
    handleChange({ type, key, value: valueParsed })
  }

  const handleDeleteItem = () => {
    handleChange({ type, key, value: undefined })
    removeProp(key)
  }

  const ItemComponent = propConfig.customInput ? propConfig.customInput : slide ? ItemSlide : ItemArbitrary

  return <>
    <div className='flex gap-2 items-center'>
      {label}
      {!onlySlide && <>
        <DefaultTooltip side='left' content={slide ? 'Show predefined options' : 'Show slide'} triggerProps={{ asChild: true }}>
          <Button size='sm' color='accent' variant={slide ? 'light' : 'ghost'} onClick={() => setSlide(!slide)}>
            <EllipsisVertical size={12} />
          </Button>
        </DefaultTooltip>
      </>}
    </div>

    <div className='flex gap-2 items-center'>
      <ItemComponent
        {...propConfig}
        value={value}
        onChange={handleChangeKey}
        type={type}
      />

      <Button variant='outline' size='sm' onClick={handleDeleteItem}>
        <Trash size='14' />
      </Button>

    </div>
    <Separator className='m-0 my-2' />
  </>
}

const ItemSlide = ({ value, onChange, defaultValue, slideConfig = {} }) => {
  useEffect(() => {
    if (value === undefined && defaultValue !== undefined) {
      onChange(defaultValue)
    }
  }, [value, onChange, defaultValue])

  const labelValue = useMemo(() => `${value?.replace('_', '')}${slideConfig.unit || ''}`, [value, slideConfig.unit])

  return <>
    <div className='relative w-full'>
      <Slider
        min={slideConfig.min || 0}
        max={slideConfig.max || 100}
        step={slideConfig.step || 1}
        value={value?.split('_')[0]}
        onChange={onChange}
        tooltip={false}
      />
      <small className='absolute left-1/2 -translate-x-1/2 top-full mt-0.5'>
        <label className='mb-0'>
          {labelValue}
        </label>
      </small>
    </div>
  </>
}

const ItemArbitrary = ({ value, onChange, predefined, predefinedOptions, defaultUnit, unitOptions = UNIT_OPTIONS }) => <>
  <PredefinedArbitraryUnit
    value={value}
    onChange={onChange}
    predefined={predefined}
    predefinedOptions={predefinedOptions}
    defaultUnit={defaultUnit}
    unitOptions={unitOptions}
  />
</>
